import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    FaSearch, FaBell, FaComments, FaCog, FaPlus, FaFilter,
    FaHeart, FaComment, FaShareAlt, FaLink, FaRobot,
    FaLightbulb, FaCheckCircle, FaChevronRight, FaGlobeAmericas,
    FaUserEdit, FaSignOutAlt, FaChevronDown, FaUsers, FaCalendarAlt,
    FaFlask, FaChartBar, FaImage, FaVideo, FaGift, FaUserFriends
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import LanguageSelector from '../components/LanguageSelector';
import { GrantsModal } from '../components/feed';
import './ResearcherCommunity.css';

const ResearcherCommunity = () => {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(t('tabs.forYou'));
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [isGrantsModalOpen, setIsGrantsModalOpen] = useState(false);
    const { token } = useAuth();

    const handleLogout = () => {
        logout();
        navigate('/landing');
    };

    const TABS = [t('tabs.forYou'), t('tabs.recent'), t('tabs.myTopics')];

    const TRENDS = [
        { category: 'CRISPR-Cas9', posts: '1.2k' },
        { category: 'Telemedicina', posts: '856' },
        { category: 'Biomateriales', posts: '432' },
        { category: 'IA en Diagnóstico', posts: '2.1k' },
    ];

    const POSTS = [
        {
            id: 1,
            author: 'Dra. Elena Martínez',
            role: 'Inmunóloga • Hospital Central',
            time: 'hace 2h',
            content: 'Acabamos de publicar los resultados preliminares del estudio sobre inmunoterapia en fase II. Los datos sugieren una tasa de respuesta del 45% en pacientes resistentes. ¡Un gran avance para nuestro equipo! 🧬✨',
            likes: 124,
            comments: 18,
            mentions: 5,
            verified: true,
            attachment: {
                image: 'https://images.unsplash.com/photo-1579152276507-03f196309252?auto=format&fit=crop&q=80&w=800',
                title: 'Reporte Preliminar Inmuno-Onco Phase II',
                desc: 'PDF • 4.2 MB • Publicado en The Lancet',
                url: 'thelancet.com/research/inmuno-2'
            }
        },
        {
            id: 2,
            author: 'Dr. Roberto Sánchez',
            role: 'Cardiólogo • BioTech Lab',
            time: 'hace 4h',
            content: '¿Alguien tiene experiencia con el nuevo sensor de presión arterial de MedTech? Estamos considerando integrarlo en nuestro próximo protocolo multicéntrico.',
            likes: 56,
            comments: 32,
            mentions: 2,
            verified: false
        }
    ];

    const LIFECYCLE_ACTIONS = [
        { icon: <FaPlus />, label: t('lifecycle.newIdea'), route: '/researcher-ideation' },
        { icon: <FaFileAlt />, label: t('lifecycle.protocol'), route: '/protocol-editor' },
        { icon: <FaClipboardList />, label: t('lifecycle.management'), route: '/clinical-projects' },
        { icon: <FaEdit />, label: t('lifecycle.aiWriting') },
    ];

    const FaFileAlt = () => <span className="icon-placeholder">📄</span>;
    const FaClipboardList = () => <span className="icon-placeholder">📋</span>;
    const FaEdit = () => <span className="icon-placeholder">📝</span>;

    return (
        <div className="rc-container">
            {/* SIDEBAR */}
            <aside className="rc-sidebar">
                <div className="rc-logo" onClick={() => navigate('/researcher-community')}>
                    <img src="/logoecseia.png" alt="Logo" className="rc-logo-icon" />
                    <span className="rc-logo-text"><span className="orange">Eco</span>Innova</span>
                </div>

                <div className="rc-nav-section">
                    <p className="rc-nav-label">{t('sidebar.platform')}</p>
                    <button className="rc-nav-item active" onClick={() => navigate('/researcher-network')}><FaUsers /> {t('sidebar.red')}</button>
                    <button className="rc-nav-item" onClick={() => navigate('/researcher-library')}><FaSearch /> {t('sidebar.scientificSearch')}</button>
                    <button className="rc-nav-item" onClick={() => navigate('/scientific-events')}><FaCalendarAlt /> {t('sidebar.eventsWebinars')}</button>
                    <button className="rc-nav-item" onClick={() => setIsGrantsModalOpen(true)}><FaGift /> {t('sidebar.grants')}</button>
                    <button className="rc-nav-item" onClick={() => navigate('/researcher-groups')}><FaUserFriends /> {t('sidebar.grupos')}</button>
                    <button className="rc-nav-item" onClick={() => navigate('/clinical-projects')}><FaFlask /> {t('sidebar.projects')}</button>
                </div>

                <div className="rc-nav-section">
                    <p className="rc-nav-label">{t('sidebar.mySpace')}</p>
                    <button className="rc-nav-item" onClick={() => navigate('/clinical-projects')}><FaFlask /> {t('sidebar.myProjects')}</button>
                    <button className="rc-nav-item"><FaHeart /> {t('sidebar.saved')}</button>
                    <button className="rc-nav-item"><FaChartBar /> {t('sidebar.analytics')}</button>
                </div>

                <div className="rc-active-trials">
                    <p className="rc-nav-label">{t('sidebar.activeStudies')}</p>
                    <div className="rc-trial-mini">
                        <div className="rc-trial-dot" style={{ background: '#22c55e' }}></div>
                        <span>Melanoma Inmuno Ph II</span>
                    </div>
                </div>

                <div className="rc-sidebar-footer">
                    <button className="rc-nav-item"><FaCog /> {t('sidebar.settings')}</button>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <div className="rc-main">
                {/* HEADER */}
                <header className="rc-header">
                    <div className="rc-search-bar">
                        <FaSearch className="rc-search-icon" />
                        <input type="text" placeholder={t('header.aiSearchPlaceholder')} />
                    </div>
                    <div className="rc-header-actions">
                        <LanguageSelector />
                        <button className="rc-icon-btn"><FaBell /></button>
                        <button className="rc-icon-btn"><FaComments /></button>
                        <div className="rc-user-pill-wrap">
                            <div className="rc-user-pill" onClick={() => setUserMenuOpen(!userMenuOpen)}>
                                <div className="rc-avatar-mini">{user?.name?.charAt(0) || 'D'}</div>
                                <div className="rc-user-info-mini">
                                    <span className="rc-user-name">{user?.name || 'Dr. Julián Rivera'}</span>
                                    <span className="rc-user-role">{user?.role || t('investigador')}</span>
                                </div>
                                <FaChevronDown className={`rc-chevron ${userMenuOpen ? 'open' : ''}`} />
                            </div>
                            {userMenuOpen && (
                                <div className="rc-dropdown-menu">
                                    <button className="rc-dropdown-item"><FaUserEdit /> {t('portal.userMenu.editProfile')}</button>
                                    <button className="rc-dropdown-item danger" onClick={handleLogout}><FaSignOutAlt /> {t('portal.userMenu.logout')}</button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <div className="rc-dashboard-grid">
                    {/* LEFT COLUMN: FEED */}
                    <div className="rc-feed">
                        {/* COMPOSER */}
                        <div className="rc-composer">
                            <div className="rc-composer-top">
                                <div className="rc-avatar-mini">{user?.name?.charAt(0) || 'D'}</div>
                                <textarea placeholder={t('composer.placeholder')}></textarea>
                            </div>
                            <div className="rc-composer-bottom">
                                <div className="rc-post-tools">
                                    <button className="rc-tool-btn"><FaImage /></button>
                                    <button className="rc-tool-btn"><FaVideo /></button>
                                    <button className="rc-tool-btn"><FaLink /></button>
                                </div>
                                <button className="rc-btn-primary">{t('composer.publish')}</button>
                            </div>
                        </div>

                        {/* TABS */}
                        <div className="rc-feed-header">
                            <div className="rc-tabs">
                                {TABS.map(tab => (
                                    <button
                                        key={tab}
                                        className={`rc-tab ${activeTab === tab ? 'active' : ''}`}
                                        onClick={() => setActiveTab(tab)}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                            <div className="rc-filter">
                                <FaFilter /> <span>{t('tabs.filter')}</span>
                            </div>
                        </div>

                        {/* POSTS */}
                        {POSTS.map(post => (
                            <div key={post.id} className="rc-post">
                                <div className="rc-post-header">
                                    <div className="rc-post-user">
                                        <div className="rc-user-img-placeholder">{post.author.charAt(0)}</div>
                                        <div className="rc-post-info">
                                            <h4>{post.author} {post.verified && <FaCheckCircle className="rc-verified" />}</h4>
                                            <p className="rc-post-meta">{post.role} • {post.time}</p>
                                        </div>
                                    </div>
                                    <button className="rc-action-btn"><FaPlus /></button>
                                </div>
                                <p className="rc-post-content">{post.content}</p>

                                {post.attachment && (
                                    <div className="rc-post-attachment">
                                        <div className="rc-attach-img-placeholder">🔬</div>
                                        <div className="rc-attach-body">
                                            <p className="rc-attach-title">{post.attachment.title}</p>
                                            <p className="rc-attach-desc">{post.attachment.desc}</p>
                                            <div className="rc-attach-link"><FaLink /> {post.attachment.url}</div>
                                        </div>
                                    </div>
                                )}

                                <div className="rc-post-interactions">
                                    <div className="rc-inter-left">
                                        <button className="rc-inter-btn"><FaHeart /> {post.likes}</button>
                                        <button className="rc-inter-btn"><FaComment /> {post.comments}</button>
                                        <button className="rc-inter-btn"><FaShareAlt /> {t('post.share')}</button>
                                    </div>
                                    <div className="rc-mentions">{post.mentions} {t('post.mentions')}</div>
                                </div>
                            </div>
                        ))}

                        {/* PROJECT PROMOTION */}
                        <div className="rc-collab-card">
                            <div className="rc-collab-header">
                                <div className="rc-post-user">
                                    <div className="rc-user-avatar-top" style={{ background: '#7c3aed' }}>UL</div>
                                    <div className="rc-post-info">
                                        <h4>{t('community.feed.suggestedGroups')}</h4>
                                        <p className="rc-post-meta">BioTech Global Hub • hace 5h</p>
                                    </div>
                                </div>
                                <span className="rc-collab-badge">{t('community.feed.join')}</span>
                            </div>
                            <h3>Proyecto Open Source: Clasificación de Microplásticos</h3>
                            <p className="rc-post-content">Estamos buscando colaboradores para entrenar modelos de detección de microplásticos en muestras de agua.</p>

                            <div className="rc-tags">
                                <span className="rc-tag">Python</span>
                                <span className="rc-tag">TensorFlow</span>
                                <span className="rc-tag">Ecología</span>
                            </div>

                            <div className="rc-collab-footer">
                                <div className="rc-avatars-stack">
                                    <div className="rc-mini-avatar-placeholder">1</div>
                                    <div className="rc-mini-avatar-placeholder">2</div>
                                    <div className="rc-avatar-count">+5</div>
                                </div>
                                <div className="rc-view-project" onClick={() => navigate('/clinical-projects')}>
                                    {t('widgets.viewRecommendation')} <FaChevronRight />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: WIDGETS */}
                    <div className="rc-right">
                        {/* ECOAI INSIGHT */}
                        <div className="rc-widget">
                            <div className="rc-insight-header">
                                <FaRobot /> {t('widgets.ecoAiInsight')}
                                <FaLightbulb className="rc-insight-ai" />
                            </div>
                            <p style={{ fontSize: '0.85rem', lineHeight: '1.4', marginBottom: '15px' }}>
                                {t('widgets.insightDesc')}
                            </p>
                            <button className="rc-btn-outline" onClick={() => navigate('/clinical-projects')}>
                                {t('widgets.viewRecommendation')}
                            </button>
                        </div>

                        {/* CICLO DE VIDA CON IA */}
                        <div className="rc-widget cycle-widget">
                            <h3 className="rc-widget-title">{t('lifecycle.title')}</h3>
                            <p className="rc-widget-desc">{t('lifecycle.subtitle')}</p>
                            <div className="rc-cycle-grid">
                                {LIFECYCLE_ACTIONS.map(action => (
                                    <button
                                        key={action.label}
                                        className="rc-cycle-item"
                                        onClick={() => action.route && navigate(action.route)}
                                    >
                                        <span className="rc-cycle-icon">{action.icon}</span>
                                        <span className="rc-cycle-label">{action.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* TENDENCIAS */}
                        <div className="rc-widget">
                            <h3 className="rc-widget-title">{t('widgets.trendsTitle')}</h3>
                            {TRENDS.map((t, i) => (
                                <div key={i} className="rc-trend-item">
                                    <p className="rc-trend-meta">{t.category} • {t.posts}</p>
                                    <p className="rc-trend-tag">#{t.category.replace(/\s+/g, '')}</p>
                                </div>
                            ))}
                            <button className="rc-text-btn">{t('widgets.viewMoreSuggestions')}</button>
                        </div>

                        {/* FOOTER WIDGET */}
                        <div className="rc-footer-widget">
                            <div className="rc-footer-links">
                                <span>{t('common.privacy')}</span>
                                <span>{t('common.terms')}</span>
                                <span>{t('common.cookies')}</span>
                            </div>
                            <p>© 2024 EcoScienceInnova</p>
                        </div>
                    </div>
                </div>
            </div>
            <GrantsModal 
                isOpen={isGrantsModalOpen} 
                onClose={() => setIsGrantsModalOpen(false)} 
                token={token} 
            />
        </div>
    );
};

export default ResearcherCommunity;
